import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, parseISO, subDays } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  X,
  TrendingUp,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Calendar,
  Plus,
  Trash2,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, formatNumber, getPlatformBadgeClass } from "./constants";

// Translations
const TRANSLATIONS = {
  en: {
    title: "Content Growth Trend",
    compare: "Compare Content",
    addToCompare: "Add to Compare",
    removeFromCompare: "Remove",
    selectPeriod: "Select Period",
    startDate: "Start",
    endDate: "End",
    apply: "Apply",
    views: "Views",
    likes: "Likes",
    comments: "Comments",
    shares: "Shares",
    saves: "Saves",
    dailyData: "Daily Data",
    date: "Date",
    noData: "No historical data available",
    loading: "Loading...",
    maxCompare: "Max 5 contents can be compared",
  },
  ko: {
    title: "콘텐츠 성장 추이",
    compare: "콘텐츠 비교",
    addToCompare: "비교 추가",
    removeFromCompare: "제거",
    selectPeriod: "기간 선택",
    startDate: "시작",
    endDate: "종료",
    apply: "적용",
    views: "조회수",
    likes: "좋아요",
    comments: "댓글",
    shares: "공유",
    saves: "저장",
    dailyData: "일별 데이터",
    date: "날짜",
    noData: "히스토리 데이터가 없습니다",
    loading: "로딩 중...",
    maxCompare: "최대 5개의 콘텐츠를 비교할 수 있습니다",
  },
  zh: {
    title: "内容增长趋势",
    compare: "内容比较",
    addToCompare: "添加比较",
    removeFromCompare: "移除",
    selectPeriod: "选择时间段",
    startDate: "开始",
    endDate: "结束",
    apply: "应用",
    views: "浏览量",
    likes: "点赞",
    comments: "评论",
    shares: "分享",
    saves: "收藏",
    dailyData: "每日数据",
    date: "日期",
    noData: "没有历史数据",
    loading: "加载中...",
    maxCompare: "最多可比较5个内容",
  },
};

// Color palette for comparison lines
const CHART_COLORS = [
  "#3b82f6", // blue
  "#ef4444", // red
  "#22c55e", // green
  "#f59e0b", // amber
  "#8b5cf6", // violet
];

// Custom tooltip
const CustomTooltip = ({ active, payload, label, metric, t }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-2">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-muted-foreground truncate max-w-[150px]">
            {entry.name}:
          </span>
          <span className="font-medium">
            {entry.value !== null ? formatNumber(entry.value) : "N/A"}
          </span>
        </div>
      ))}
    </div>
  );
};

// Metric tabs configuration
const METRICS = [
  { key: "views", icon: Eye, color: "text-blue-500", bgColor: "bg-blue-500" },
  { key: "likes", icon: Heart, color: "text-red-500", bgColor: "bg-red-500" },
  { key: "comments", icon: MessageCircle, color: "text-amber-500", bgColor: "bg-amber-500" },
  { key: "shares", icon: Share2, color: "text-purple-500", bgColor: "bg-purple-500" },
  { key: "saves", icon: Bookmark, color: "text-green-500", bgColor: "bg-green-500" },
];

const GrowthTrendModal = ({
  isOpen,
  onClose,
  initialPost,
  availablePosts = [],
  lang = "en",
  minDate = null,
}) => {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  // State
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [activeMetric, setActiveMetric] = useState("views");
  const [startDate, setStartDate] = useState(() => subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(() => new Date());
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPostSelector, setShowPostSelector] = useState(false);

  // Initialize with initial post
  useEffect(() => {
    if (isOpen && initialPost) {
      setSelectedPosts([initialPost]);
    }
  }, [isOpen, initialPost]);

  // Set min date from props
  useEffect(() => {
    if (minDate) {
      const minDateObj = typeof minDate === 'string' ? parseISO(minDate) : minDate;
      if (startDate < minDateObj) {
        setStartDate(minDateObj);
      }
    }
  }, [minDate]);

  // Fetch comparison data
  const fetchComparisonData = useCallback(async () => {
    if (selectedPosts.length === 0) {
      setChartData([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/analytics/posts-compare`, {
        post_ids: selectedPosts.map((p) => p.post_id || p.id),
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
      });

      const { posts, dates } = response.data;

      // Transform data for recharts
      const transformed = dates.map((date) => {
        const entry = { date: date.slice(5) }; // MM-DD format
        posts.forEach((post, idx) => {
          const snapshot = post.history.find((h) => h.date === date);
          // Use post title (truncated) as key
          const key = post.title.slice(0, 20) + (post.title.length > 20 ? "..." : "");
          entry[key] = snapshot ? snapshot[activeMetric] : null;
        });
        return entry;
      });

      setChartData(transformed);
    } catch (error) {
      console.error("Failed to fetch comparison data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedPosts, startDate, endDate, activeMetric]);

  useEffect(() => {
    if (isOpen && selectedPosts.length > 0) {
      fetchComparisonData();
    }
  }, [isOpen, selectedPosts, activeMetric, fetchComparisonData]);

  // Add post to comparison
  const addPostToCompare = (post) => {
    if (selectedPosts.length >= 5) return;
    if (selectedPosts.some((p) => (p.post_id || p.id) === (post.post_id || post.id))) return;
    setSelectedPosts([...selectedPosts, post]);
    setShowPostSelector(false);
  };

  // Remove post from comparison
  const removePost = (postId) => {
    setSelectedPosts(selectedPosts.filter((p) => (p.post_id || p.id) !== postId));
  };

  // Disable dates before min snapshot date
  const disabledDates = (date) => {
    if (!minDate) return false;
    const minDateObj = typeof minDate === 'string' ? parseISO(minDate) : minDate;
    return date < minDateObj;
  };

  // Get line keys from chart data
  const getLineKeys = () => {
    if (chartData.length === 0) return [];
    return Object.keys(chartData[0]).filter((k) => k !== "date");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Selected Posts & Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Selected posts badges */}
            {selectedPosts.map((post, idx) => (
              <Badge
                key={post.post_id || post.id}
                variant="secondary"
                className="flex items-center gap-1 py-1 px-2"
                style={{ borderLeftColor: CHART_COLORS[idx], borderLeftWidth: 3 }}
              >
                <span className="truncate max-w-[120px]">{post.title?.slice(0, 15)}...</span>
                <button
                  onClick={() => removePost(post.post_id || post.id)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

            {/* Add to compare button */}
            {selectedPosts.length < 5 && availablePosts.length > 0 && (
              <Popover open={showPostSelector} onOpenChange={setShowPostSelector}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7">
                    <Plus className="h-3 w-3 mr-1" />
                    {t.addToCompare}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <ScrollArea className="h-[300px]">
                    <div className="p-2 space-y-1">
                      {availablePosts
                        .filter((p) => !selectedPosts.some((sp) => (sp.post_id || sp.id) === (p.post_id || p.id)))
                        .slice(0, 20)
                        .map((post) => (
                          <button
                            key={post.post_id || post.id}
                            onClick={() => addPostToCompare(post)}
                            className="w-full text-left p-2 rounded hover:bg-accent flex items-center gap-2"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{post.title}</p>
                              <p className="text-xs text-muted-foreground">{post.platform}</p>
                            </div>
                          </button>
                        ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            )}

            {/* Period selector */}
            <div className="ml-auto flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7">
                    <Calendar className="h-3 w-3 mr-1" />
                    {format(startDate, "MM/dd")} - {format(endDate, "MM/dd")}
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="end">
                  <div className="flex gap-4">
                    <div>
                      <p className="text-xs font-medium mb-1">{t.startDate}</p>
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        onSelect={(d) => d && setStartDate(d)}
                        disabled={disabledDates}
                        initialFocus
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-1">{t.endDate}</p>
                      <CalendarComponent
                        mode="single"
                        selected={endDate}
                        onSelect={(d) => d && setEndDate(d)}
                        disabled={disabledDates}
                        initialFocus
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button size="sm" onClick={fetchComparisonData}>
                      {t.apply}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              <Button variant="ghost" size="sm" className="h-7" onClick={fetchComparisonData} disabled={loading}>
                <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {/* Metric Tabs */}
          <Tabs value={activeMetric} onValueChange={setActiveMetric} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-5 w-full max-w-md">
              {METRICS.map(({ key, icon: Icon, color }) => (
                <TabsTrigger key={key} value={key} className="flex items-center gap-1">
                  <Icon className={`h-3.5 w-3.5 ${color}`} />
                  <span className="hidden sm:inline">{t[key]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Chart Area */}
            <div className="flex-1 mt-4 min-h-[300px]">
              {loading ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                  {t.loading}
                </div>
              ) : chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  {t.noData}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      className="text-muted-foreground"
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      tickFormatter={(v) => formatNumber(v)}
                      className="text-muted-foreground"
                    />
                    <Tooltip content={<CustomTooltip metric={activeMetric} t={t} />} />
                    <Legend />
                    {getLineKeys().map((key, idx) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Daily Data Table */}
            {chartData.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">{t.dailyData}</h4>
                <ScrollArea className="h-[150px] border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="sticky left-0 bg-background">{t.date}</TableHead>
                        {getLineKeys().map((key, idx) => (
                          <TableHead key={key} className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                              />
                              <span className="truncate max-w-[80px]">{key}</span>
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {chartData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="sticky left-0 bg-background font-medium">
                            {row.date}
                          </TableCell>
                          {getLineKeys().map((key) => (
                            <TableCell key={key} className="text-right">
                              {row[key] !== null ? formatNumber(row[key]) : "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GrowthTrendModal;
